import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const START_05: GgxTourDefinition = {
    key: '[START-05] Settings & Governance Configurations',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 2,
    intro: {
        headline: 'Customizing the Platform for Your Needs',
        subtitle:
            'GGX offers flexible configuration options to tailor the platform to your ' +
            "organization's specific needs. These settings allow you to adjust workflows, " +
            'permissions, and governance structures for a customized experience.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            "In this tour, you've seen how the Settings section gives you full control over " +
            'the GGX platform, how integrations extend what it can do, and how project-level ' +
            'roles and members keep access scoped to the right people.',
        learned: [
            '**Org Settings** is where administrators configure the platform.',
            '**Integrations** connect LLM, agent and report providers.',
            '**Roles and members** control access per project.',
        ],
    },
    chapters: [
        {
            name: 'Open Org Settings',
            blurb: 'Where administrators configure the platform',
            icon: 'map',
            steps: [2, 3],
        },
        {
            name: 'Platform Integrations',
            blurb: 'LLM, agent, report and other providers',
            icon: 'pulse',
            steps: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        },
        {
            name: 'Project access & roles',
            blurb: 'Per-project roles, permissions and members',
            icon: 'compass',
            steps: [13, 14, 15, 16, 17, 18, 19, 20],
        },
    ],
    steps: [
        {
            target: '[test-id="profile-details"]',
            title: "Let's make GGX fit your organization",
            whatHappens: 'your profile menu opens.',
            icon: 'grid',
            description:
                'Every organization works a little differently, and Settings is where GGX adapts ' +
                'to yours. Here administrators manage users and their permissions, set up ' +
                'approval workflows, organize pipelines and models into groups, and connect the ' +
                'tools your teams already use. It all starts from your profile menu. Click your ' +
                '<strong>avatar</strong> in the top right.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'your avatar',
        },
        {
            target: '[test-id="profile-menu-item-Org Settings"]',
            title: 'Org Settings is the admin area',
            whatHappens: 'the Org Settings page opens with its sections in the sidebar.',
            icon: 'map',
            description:
                'Org Settings holds the controls that apply to the whole organization. Access is ' +
                'usually limited to a few administrators who own policies and security, so if ' +
                'you cannot see some of these pages later, that is by design. Click <strong>Org ' +
                'Settings</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Org Settings',
        },
        {
            target: '[test-id="settings-menu-item-Integrations"]',
            title: 'Connect the tools you already use',
            whatHappens: 'the Integrations page opens, organized by provider type.',
            icon: 'pulse',
            description:
                'GGX is built to fit into your existing stack rather than replace it. ' +
                'Integrations is where you connect language model providers, agent platforms, ' +
                'notebooks, monitoring tools and your company sign-on. Click ' +
                '<strong>Integrations</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Integrations',
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'LLM Providers',
            section: true,
            title: 'Start with your language models',
            whatHappens: 'next, agent integrations.',
            icon: 'grid',
            description:
                '<strong>LLM Providers</strong> is where you connect the models your pipelines ' +
                'will call: OpenAI, Google Gemini, Anthropic, DeepSeek, HuggingFace and others. ' +
                'Once a provider is connected here, its models appear in the pipeline builder ' +
                'for everyone in the organization to use. Click Continue.',
            side: 'left',
            align: 'start',
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'Agent Integrations',
            section: true,
            title: 'Bring in agents built elsewhere',
            whatHappens: 'next, IDE and notebook integrations.',
            icon: 'compass',
            description:
                'If your teams already build agents on platforms like Vertex AI Agent Playbooks, ' +
                'Salesforce AgentForce, Microsoft Copilot Studio or Vapi AI, <strong>Agent ' +
                'Integrations</strong> lets GGX connect to them, so they can be tested and ' +
                'governed here like any other pipeline. Click Continue.',
            side: 'left',
            align: 'start',
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'IDE/Notebook Integrations',
            section: true,
            title: 'Work from the tools your team lives in',
            whatHappens: 'next, report providers.',
            icon: 'cap',
            description:
                'Data scientists often prefer notebooks and IDEs. <strong>IDE/Notebook ' +
                'Integrations</strong> connects JupyterHub, Google Colab, VS Code and similar ' +
                'tools, so people can build and explore in the environment they know and ' +
                'register the results in GGX. Click Continue.',
            side: 'left',
            align: 'start',
            draftCopy: true,
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'Report Providers',
            section: true,
            title: 'Add outside evaluation tools',
            whatHappens: 'next, guardrail providers.',
            icon: 'list',
            description:
                'GGX ships its own reports, and <strong>Report Providers</strong> lets you add ' +
                'third-party evaluation services such as CleanLabs and Perspective API. Their ' +
                'scores show up alongside the built-in ones when you run an evaluation. Click ' +
                'Continue.',
            side: 'left',
            align: 'start',
            draftCopy: true,
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'Guardrail Providers',
            section: true,
            title: 'Guardrails check every call',
            whatHappens: 'next, observability providers.',
            icon: 'search',
            description:
                'A guardrail is a rule that runs before a request reaches the model or after the ' +
                'reply comes back, blocking things like personal data leaks or off-limits ' +
                'topics. <strong>Guardrail Providers</strong> is where you connect the services ' +
                'that enforce those rules across your pipelines. Click Continue.',
            side: 'left',
            align: 'start',
            draftCopy: true,
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'Observability Providers',
            section: true,
            title: 'Watch it run in production',
            whatHappens: 'next, voice providers.',
            icon: 'pulse',
            description:
                'Once a pipeline is live, you need to see how it behaves with real traffic. ' +
                '<strong>Observability Providers</strong> connects monitoring tools like Weights ' +
                '& Biases and Fiddler AI, so usage, latency and quality signals flow to the ' +
                'dashboards your teams already watch. Click Continue.',
            side: 'left',
            align: 'start',
            draftCopy: true,
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'Voice Providers',
            section: true,
            title: 'Give your pipelines a voice',
            whatHappens: 'next, single sign-on.',
            icon: 'compass',
            description:
                'For phone or voice assistants, <strong>Voice Providers</strong> connects ' +
                'speech-to-text, text-to-speech and voice agent services. The pipeline logic ' +
                'stays the same; these providers handle turning speech into text and back. Click ' +
                'Continue.',
            side: 'left',
            align: 'start',
            draftCopy: true,
        },
        {
            target: '[test-id^="integration-section-"]',
            targetText: 'Single Sign On (SSO)',
            section: true,
            title: 'One login for everyone',
            whatHappens: 'next we leave Org Settings and look at project-level access.',
            icon: 'map',
            description:
                '<strong>Single Sign On</strong> connects GGX to your company identity provider ' +
                'through SAML or Active Directory. People sign in with the account they already ' +
                'have, and your security team keeps control of who has access. Click Continue.',
            side: 'left',
            align: 'start',
            draftCopy: true,
        },
        {
            target: '[test-id="project-switcher"]',
            closeSettings: true,
            title: 'Some settings live inside a project',
            whatHappens: 'a list of your projects opens.',
            icon: 'grid',
            description:
                'Org Settings covers the whole organization, but access is usually decided per ' +
                'project. Each project has its own roles and members, so a team only sees the ' +
                'pipelines and data it works on. To see that, we need to step into a project. ' +
                'Click the <strong>project switcher</strong> at the top.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'the project switcher',
            draftCopy: true,
        },
        {
            target: '[test-id^="project-list-option-"]',
            targetText: 'Credit Card Classification',
            scrollIntoView: true,
            title: 'We will use Credit Card Classification',
            whatHappens: 'the project home opens, with Manage Project in the sidebar.',
            icon: 'map',
            description:
                'Roles and members belong to one project at a time, so the rest of this tour ' +
                'happens inside <strong>Credit Card Classification</strong>, which already has a ' +
                'sample role and members set up. Click it in the list.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            advanceToProjectHome: true,
            actionLabel: 'Credit Card Classification',
            draftCopy: true,
        },
        {
            target: '[test-id="sidenav-item-tab-Project Settings"]',
            scrollIntoView: true,
            title: 'Who can do what in this project?',
            whatHappens: 'Project Settings opens on the Project Roles page.',
            icon: 'map',
            description:
                'Under Manage Project, <strong>Project Settings</strong> is where project admins ' +
                'decide who can see and change what. It is kept separate from Org Settings on ' +
                'purpose, so each team can manage its own access without needing an organization ' +
                'admin. Click <strong>Project Settings</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Project Settings',
            draftCopy: true,
        },
        {
            target: '[test-id="role-list"]',
            title: 'Roles are permission bundles',
            whatHappens: 'next we open a role to see what it grants.',
            icon: 'list',
            description:
                'Rather than setting permissions person by person, you define a role once, such ' +
                'as Reviewer or Sandbox User, and give it to as many people or teams as you ' +
                'like. Each role applies only to this project. Click Continue.',
            side: 'left',
            align: 'start',
            frame: true,
            draftCopy: true,
        },
        {
            target: '[test-id^="grid-row-link-Sandbox User"]',
            title: "Let's look inside one",
            whatHappens: 'the role opens, listing what it allows section by section.',
            icon: 'compass',
            description:
                'The clearest way to understand a role is to open it. <strong>Sandbox ' +
                'User</strong> is a sample role for people who experiment but should not approve ' +
                'anything. Click it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Sandbox User',
            draftCopy: true,
        },
        {
            target: '[test-id="object-access"]',
            title: 'Four levels for every section',
            whatHappens: 'next, the people who hold these roles.',
            icon: 'grid',
            description:
                'For each section of GGX, a role sets four things: whether the page is ' +
                '<strong>visible</strong> at all, whether the person can <strong>read</strong>, ' +
                'whether they can <strong>write</strong>, and whether they can ' +
                '<strong>approve</strong>. The switches at the top add two more: whether they ' +
                "may upload data and whether they may manage this project's settings. Hidden " +
                'pages simply do not appear for that person. Click Continue.',
            side: 'left',
            align: 'start',
            frame: true,
            draftCopy: true,
        },
        {
            target: '[test-id="project-settings-menu-item-Project Members"]',
            title: 'Now, who is on the project?',
            whatHappens: 'the members page opens.',
            icon: 'map',
            description:
                'Roles only matter once they are given to someone. <strong>Project ' +
                'Members</strong> is where you add people to this project and choose their ' +
                'roles. Click <strong>Project Members</strong> in the sidebar.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Project Members',
            draftCopy: true,
        },
        {
            target: '[test-id="project-direct-access"]',
            title: 'Add people one at a time, or as a team',
            whatHappens: 'the tour wraps up with a summary.',
            icon: 'compass',
            description:
                '<strong>Add User</strong> gives one person access and assigns their roles. For ' +
                'larger groups, create a <strong>team</strong>, add people to it and give the ' +
                'team its roles, so a new joiner gets the right access just by joining the team. ' +
                'Team access and organization-wide access are listed below. Click Continue to ' +
                'finish.',
            side: 'left',
            align: 'start',
            frame: true,
            draftCopy: true,
        },
    ],
};
